import {Category, Menu, Product} from "@/data/entities/menu";
import {Amount, FLAGS, FlagName, ProductEditValues, toAmount} from "@/data/entities/menuEditor";
import {emptyToNull} from "@/data/Validation";

/**
 * An amount from the backend (`"12.50"`) as shown in an input (`"12,50"`).
 */
const toInputAmount = (amount: Amount): string => amount.replace(".", ",");

const isKnownFlag = (flag: string): flag is FlagName => (FLAGS as readonly string[]).includes(flag);

/**
 * Initial form values: the product's current values, or the defaults of a new
 * product in the given category.
 */
export const toProductFormValues = (
    product: Product | null,
    menu: Menu,
    category: Category,
): ProductEditValues => product === null
    ? {
        name: "",
        description: null,
        price: "",
        priceTakeaway: "",
        deposit: "0,00",
        supFee: "0,00",
        vat: 9,
        soldout: false,
        delivery: true,
        takeaway: true,
        flags: [],
        optionGroupIds: [],
        categoryIds: [category.id],
    }
    : {
        name: product.name,
        description: product.description || null,
        price: toInputAmount(product.price),
        priceTakeaway: toInputAmount(product.priceTakeaway),
        deposit: toInputAmount(product.deposit),
        supFee: toInputAmount(product.supFee),
        vat: product.vat,
        soldout: product.soldout,
        delivery: product.availability.includes("delivery"),
        takeaway: product.availability.includes("takeaway"),
        // Flags the backend doesn't know (anymore) would be rejected
        flags: product.flags.filter(isKnownFlag),
        optionGroupIds: [...product.optionGroupIds],
        // All categories it's in: the update replaces them, so a category left
        // out would lose the product
        categoryIds: menu.categories
            .filter(c => c.productIds.includes(product.id))
            .map(c => c.id),
    };

/**
 * The form values as the backend expects them.
 */
export const toProductRequest = (form: ProductEditValues): ProductEditValues => ({
    ...form,
    name: form.name.trim(),
    description: emptyToNull(form.description),
    price: toAmount(form.price),
    priceTakeaway: toAmount(form.priceTakeaway),
    deposit: toAmount(form.deposit),
    supFee: toAmount(form.supFee),
});